import { useResourceList } from '@/shared/api/jsonapi/hooks';
import type { MRT_ColumnFiltersState, MRT_PaginationState, MRT_SortingState } from 'mantine-react-table';
import type { ListQuery, FilterClause } from '@/shared/api/jsonapi/types';
import type { Denormalized } from '@/shared/api/jsonapi/parser';

interface Options {
  pagination: MRT_PaginationState;
  sorting: MRT_SortingState;
  filters: MRT_ColumnFiltersState;
  globalFilter?: string;
  globalFilterFields?: string[];
  include?: string[];
  fields?: Record<string, string[]>;
}

/**
 * Bridge mantine-react-table state → JSON:API list query.
 * - Sort: maps "id" desc → "-id"
 * - Filter: defaults to `like` for strings, `eq` for non-string columns
 * - Global filter: applied to `globalFilterFields` joined by OR is NOT supported by BE,
 *   so we fall back to `like` on the first field as a best effort.
 */
export function useJsonApiTable<T extends Denormalized = Denormalized>(type: string, opts: Options) {
  const { pagination, sorting, filters, globalFilter, globalFilterFields, include, fields } = opts;

  const sort: string[] = sorting.map((s) => `${s.desc ? '-' : ''}${s.id}`);

  const filter: Record<string, FilterClause> = {};
  for (const f of filters) {
    if (f.value == null || f.value === '') continue;
    if (typeof f.value === 'string') {
      filter[f.id] = { like: f.value };
    } else if (typeof f.value === 'number' || typeof f.value === 'boolean') {
      filter[f.id] = { eq: f.value };
    } else if (Array.isArray(f.value) && f.value.length) {
      filter[f.id] = { in: f.value as Array<string | number> };
    }
  }
  if (globalFilter && globalFilterFields?.length) {
    filter[globalFilterFields[0]] = { like: globalFilter };
  }

  const query: ListQuery = {
    page: { number: pagination.pageIndex + 1, size: pagination.pageSize },
    sort: sort.length ? sort : undefined,
    filter: Object.keys(filter).length ? filter : undefined,
    include,
    fields,
  };

  return useResourceList<T>(type, query);
}
